  import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import policeDashboardService from '../lib/policeDashboardService';
import api, { extractApiError } from '@/lib/api';
import { subscribeToIncidents } from '@/services/incidentService';
import { toast } from 'sonner';

const PoliceDashboardContext = createContext();

const normalizeIncident = (item) => {
  const primaryVehicle = Array.isArray(item?.vehicles)
    ? item.vehicles.find((v) => v.role === 'PRIMARY')
    : null;

  return {
    id: item?.id || item?.incident_id || '',
    type: item?.incident_type || item?.type || 'Unknown Alert',
    severity: String(item?.severity || 'low').toLowerCase(),
    location: item?.location || item?.address || item?.place || 'Unknown location',
    vehicle: primaryVehicle?.registration_number || item?.vehicle || 'Unassigned',
    driver: item?.driver?.name || item?.driver || 'Unknown driver',
    reportedAt: item?.event_time || item?.created_at || item?.timestamp || new Date().toISOString(),
    raw: item,
  };
};

const playAlertTone = () => {
  if (typeof window === 'undefined' || !window.AudioContext) return;
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gain.gain.value = 0.08;
    oscillator.connect(gain);
    gain.connect(audioCtx.destination);
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      audioCtx.close();
    }, 180);
  } catch (error) {
    console.error('Alert tone failed', error);
  }
};

export const usePoliceDashboard = () => {
  const context = useContext(PoliceDashboardContext);
  if (!context) {
    throw new Error('usePoliceDashboard must be used within a PoliceDashboardProvider');
  }
  return context;
};

export const PoliceDashboardProvider = ({ children }) => {
  const [data, setData] = useState({
    alerts: [],
    activeCases: [],
    completedCases: [],
    units: [],
    overview: {
      activeCases: 0,
      newAlerts: 0,
      completedCases: 0,
      highPriorityAlerts: 0,
    },
  });
  const [featuredCase, setFeaturedCase] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [apiReady, setApiReady] = useState(false);

  // Do not seed provider state with mock service data to avoid silently
  // showing fake alerts to consumers. `fetchOpenAlerts` will populate
  // `alerts` from the API; on failure `alerts` remains an honest empty list.

  const refreshServiceData = () => {
    setData(policeDashboardService.getData());
  };

  const fetchOpenAlerts = async () => {
    try {
      const { data: response } = await api.get('/incidents', {
        params: { status: 'OPEN', page: 1, page_size: 20 },
      });
      const items = Array.isArray(response) ? response : response?.data || [];
      setAlerts(items.map(normalizeIncident).filter((item) => item.id));
      setApiReady(true);
    } catch (err) {
      console.error('Failed to load police open alerts', err);
      setAlerts([]);
      setApiReady(true);
    }
  };

  useEffect(() => {
    fetchOpenAlerts();

    // Subscribe to the shared WS singleton (incidentService) — never open a
    // second connection; the server allows only one WS per token per session.
    const unsubscribe = subscribeToIncidents((data) => {
      const incident = normalizeIncident(data);
      if (!incident.id) return;
      setAlerts((prev) => {
        if (prev.some((item) => item.id === incident.id)) return prev;
        playAlertTone();
        return [incident, ...prev].slice(0, 20);
      });
    });

    return () => unsubscribe();
  }, []);

  const acceptAlert = async (alertId) => {
    try {
      await api.post(`/incidents/${alertId}/service-response`, { status: 'ACKNOWLEDGED' });
      setAlerts((prev) => prev.filter((item) => item.id !== alertId));
      refreshServiceData();
      return { id: alertId };
    } catch (err) {
      console.error('Failed to acknowledge alert', err);
      return null;
    }
  };

  const dismissAlert = async (alertId) => {
    let previousAlerts = null;
    setAlerts((prev) => {
      previousAlerts = prev;
      return prev.filter((item) => item.id !== alertId);
    });

    try {
      await api.post(`/incidents/${alertId}/service-response`, { status: 'ACKNOWLEDGED' });
      await fetchOpenAlerts();
    } catch (err) {
      const { message } = extractApiError(err);
      if (previousAlerts) {
        setAlerts(previousAlerts);
      }
      toast.error(message || 'Failed to dismiss alert.');
    }
  };

  const assignUnit = async (caseId, unitId) => {
    const previousData = data;

    setData((prev) => {
      const nextActiveCases = prev.activeCases.map((caseItem) =>
        caseItem.id === caseId ? { ...caseItem, assignedUnit: unitId } : caseItem,
      );
      const nextUnits = prev.units.map((unit) => {
        if (unit.id === unitId) {
          return { ...unit, status: 'busy', currentLoad: caseId };
        }
        if (unit.currentLoad === caseId && unit.id !== unitId) {
          return { ...unit, status: 'available', currentLoad: null };
        }
        return unit;
      });

      return {
        ...prev,
        activeCases: nextActiveCases,
        units: nextUnits,
      };
    });

    try {
      await api.post(`/incidents/${caseId}/service-response`, {
        status: 'DISPATCHED',
        note: `Assigned to unit ${unitId}`,
      });
    } catch (err) {
      const { message } = extractApiError(err);
      setData(previousData);
      toast.error(message || 'Failed to assign unit.');
    }
  };

  // `startNavigation` and `transferCase` were mock-only helpers in the
  // previous local dashboard simulation and have been removed to avoid
  // carrying mock-only workflows in the provider. Real navigation/transfer
  // workflows should be implemented against backend APIs when available.

  const completeCase = async (caseId) => {
    const previousData = data;

    setData((prev) => {
      const caseItem = prev.activeCases.find((c) => c.id === caseId);
      if (!caseItem) return prev;

      const nextActiveCases = prev.activeCases.filter((c) => c.id !== caseId);
      const completedCase = {
        id: caseItem.id,
        summary: `${caseItem.type} at ${caseItem.location}`,
        severity: caseItem.severity,
        completedAt: new Date().toISOString(),
      };
      const nextCompletedCases = [completedCase, ...prev.completedCases].slice(0, 20);
      const nextUnits = prev.units.map((unit) =>
        unit.currentLoad === caseId ? { ...unit, status: 'available', currentLoad: null } : unit,
      );

      return {
        ...prev,
        activeCases: nextActiveCases,
        completedCases: nextCompletedCases,
        units: nextUnits,
        overview: {
          ...prev.overview,
          activeCases: nextActiveCases.length,
          completedCases: nextCompletedCases.length,
        },
      };
    });

    try {
      await api.post(`/incidents/${caseId}/service-response`, {
        status: 'RESOLVED',
        note: 'Terminal response logged from Police Dashboard provider',
      });
    } catch (err) {
      const { message } = extractApiError(err);
      setData(previousData);
      toast.error(message || 'Failed to complete case.');
    }
  };

  const setFeatured = (caseId) => {
    const caseItem = data.activeCases.find(c => c.id === caseId);
    setFeaturedCase(caseItem);
  };

  const mergedAlerts = apiReady ? alerts : data.alerts;

  const value = {
    ...data,
    alerts: mergedAlerts,
    overview: {
      ...data.overview,
      newAlerts: mergedAlerts.length,
    },
    featuredCase,
    actions: {
      acceptAlert,
      dismissAlert,
      assignUnit,
      completeCase,
      setFeatured,
    },
  };

  return (
    <PoliceDashboardContext.Provider value={value}>
      {children}
    </PoliceDashboardContext.Provider>
  );
};